import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SEO, breadcrumbSchema, faqSchema } from '../components/SEO';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import { Reveal } from '../components/Reveal';
import { SERVICE_CITIES } from '../data/serviceCities';
import { getService } from '../data/services';
import { getLocation } from '../data/locations';
import { SITE } from '../data/site';
import NotFoundPage from './NotFoundPage';

const ServiceCityPage = () => {
  const { slug, city } = useParams();
  const page = SERVICE_CITIES.find((sc) => sc.serviceSlug === slug && sc.path === `/services/${slug}/${city}/`);
  if (!page) return <NotFoundPage />;
  const service = getService(page.serviceSlug);
  const loc = getLocation(page.citySlug);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: page.title,
    description: page.metaDescription,
    provider: { '@type': 'HomeAndConstructionBusiness', name: SITE.name, telephone: SITE.phone },
    areaServed: `${loc.city}, Nebraska`,
    url: `${SITE.baseUrl}${page.path}`,
  };

  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={page.path}
        image={service.image.src}
        jsonLd={[
          schema,
          faqSchema(page.faqs),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services/' },
            { name: service.navTitle, path: `/services/${service.slug}/` },
            { name: loc.city, path: page.path },
          ]),
        ]}
      />
      <PageHero
        overline={`${service.navTitle} · ${loc.city}, NE`}
        title={page.headline}
        intro={page.intro}
        image={service.image}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services/' },
          { name: service.navTitle, path: `/services/${service.slug}/` },
          { name: loc.city },
        ]}
      />

      <section className="py-20 md:py-24">
        <div className="container-site max-w-4xl">
          {page.sections.map((sec, i) => (
            <Reveal key={sec.h} className={i > 0 ? 'mt-12' : ''}>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">{sec.h}</h2>
              {sec.body?.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-muted2 md:text-lg">{p}</p>
              ))}
              {sec.list && (
                <ul className="mt-5 space-y-3">
                  {sec.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-base text-muted2">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-bronze" /> {item}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
          <Reveal className="mt-12 border border-line bg-paper2/50 p-7">
            <p className="text-sm leading-relaxed text-muted2">
              Related reading: explore our full <Link to={`/services/${service.slug}/`} className="font-semibold text-bronze underline decoration-bronze/40 underline-offset-4">{service.navTitle.toLowerCase()} service</Link>, our work across <Link to={`/service-areas/${loc.slug}/`} className="font-semibold text-bronze underline decoration-bronze/40 underline-offset-4">{loc.city}</Link>, and recent <Link to="/projects/" className="font-semibold text-bronze underline decoration-bronze/40 underline-offset-4">project showcases</Link> — or browse the <Link to="/blog/" className="font-semibold text-bronze underline decoration-bronze/40 underline-offset-4">planning guides on our blog</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="container-site max-w-4xl">
          <Reveal><FAQSection faqs={page.faqs} heading={`${service.navTitle} in ${loc.city} — FAQs`} /></Reveal>
        </div>
      </section>

      <CTASection
        title={`Get a free ${service.navTitle.toLowerCase()} quote in ${loc.city}`}
        text={`Free on-site consultations across ${loc.city} and the ${loc.region}. Itemized pricing, one accountable team, 22+ years of Nebraska craftsmanship.`}
        testId={`service-city-${page.slug}-cta`}
      />
    </>
  );
};

export default ServiceCityPage;
